import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';

// Utility records staging for a records job: drop files, tag them (how they
// were collected + which utility + owner/contact), keep them on the site, then
// "Submit to Site Tracker" bundles everything into one zip.

const ALLOWED = '.pdf,.jpg,.jpeg,.png,.tif,.tiff,.kmz,.kml,.shp,.dwg,.zip';
const ALLOWED_EXTS = ['pdf', 'jpg', 'jpeg', 'png', 'tif', 'tiff', 'kmz', 'kml', 'shp', 'dwg', 'zip'];
// 'Multiple' is for a single PDF/screenshot that shows several utilities at once;
// it isn't a Records Note checkbox, so those files don't auto-fill the note.
const UTILITIES = ['Gas', 'Water', 'Storm', 'Sanitary', 'Electric', 'Pipeline', 'Communication', 'Fiber', 'Multiple'];
const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];
const SOURCES = [
  { value: 'owner', label: 'Utility owner provided', hint: 'Received directly from the utility — contact goes on the report' },
  { value: 'third_party', label: 'Authentic 3rd party', hint: 'Trusted 3rd party data' },
  { value: 'public', label: 'Publicly available', hint: 'GIS portal, county records, etc.' },
];
const SOURCE_LABEL = Object.fromEntries(SOURCES.map((s) => [s.value, s.label]));
const SOURCE_ORDER = ['owner', 'third_party', 'public'];

const inputStyle = { backgroundColor: '#0f1a29', borderColor: '#1e3a5f' };
const emptyTag = () => ({ source: 'owner', utility_type: 'Gas', owner: '', state: '', city: '', contact_name: '', contact_info: '' });

function formatSize(bytes) {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
function fmtDateTime(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });
}

async function downloadBlob(url, fallbackName) {
  const res = await api.get(url, { responseType: 'blob' });
  const cd = res.headers['content-disposition'] || '';
  const m = /filename="?([^";]+)"?/.exec(cd);
  const name = m ? decodeURIComponent(m[1]) : fallbackName;
  const href = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement('a');
  a.href = href;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(href);
}

// The tag form — used for a new drop and for retagging one file.
function TagForm({ tag, onChange, compact = false }) {
  const set = (patch) => onChange({ ...tag, ...patch });
  const isOwner = tag.source === 'owner';
  return (
    <div className="space-y-2">
      <div className={`grid gap-2 ${compact ? 'grid-cols-3' : 'grid-cols-1 sm:grid-cols-3'}`}>
        {SOURCES.map((s) => (
          <label
            key={s.value}
            className="flex items-start gap-2 rounded border px-2 py-1.5 cursor-pointer transition"
            style={{ ...inputStyle, borderColor: tag.source === s.value ? '#c9a84c' : '#1e3a5f' }}
          >
            <input type="radio" name={`source-${compact ? 'edit' : 'new'}`} checked={tag.source === s.value} onChange={() => set({ source: s.value })} className="mt-0.5 accent-yellow-500" />
            <span>
              <span className="block text-xs font-bold text-white">{s.label}</span>
              {!compact && <span className="block text-[11px] text-gray-500 leading-tight">{s.hint}</span>}
            </span>
          </label>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <select value={tag.utility_type} onChange={(e) => set({ utility_type: e.target.value })} className="text-xs rounded px-2 py-1.5 border w-32 text-white focus:outline-none" style={inputStyle} title="Utility type">
          {UTILITIES.map((u) => <option key={u} value={u}>{u}</option>)}
        </select>
        <input value={tag.owner} onChange={(e) => set({ owner: e.target.value })} placeholder="Utility owner (company)" className="flex-1 min-w-[140px] text-xs rounded px-2 py-1.5 border text-white placeholder-gray-600 focus:outline-none" style={inputStyle} />
        <select value={tag.state} onChange={(e) => set({ state: e.target.value })} className={`text-xs rounded px-1 py-1.5 border w-16 focus:outline-none ${tag.state ? 'text-white' : 'text-gray-600'}`} style={inputStyle} title="State">
          <option value="">State</option>
          {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input value={tag.city} onChange={(e) => set({ city: e.target.value })} placeholder="City" className="w-28 text-xs rounded px-2 py-1.5 border text-white placeholder-gray-600 focus:outline-none" style={inputStyle} />
      </div>
      {isOwner && (
        <div className="flex flex-wrap gap-2">
          <input value={tag.contact_name} onChange={(e) => set({ contact_name: e.target.value })} placeholder="Contact name (who sent it)" className="flex-1 min-w-[140px] text-xs rounded px-2 py-1.5 border text-white placeholder-gray-600 focus:outline-none" style={inputStyle} />
          <input value={tag.contact_info} onChange={(e) => set({ contact_info: e.target.value })} placeholder="Phone / email" className="flex-1 min-w-[140px] text-xs rounded px-2 py-1.5 border text-white placeholder-gray-600 focus:outline-none" style={inputStyle} />
        </div>
      )}
      {tag.utility_type === 'Multiple' && (
        <p className="text-[11px] text-yellow-500">Multiple utilities in one file — the Records Note isn't auto-filled for these. Check each utility there yourself.</p>
      )}
      {!isOwner && !compact && tag.utility_type !== 'Multiple' && (
        <p className="text-[11px] text-gray-500">No contact needed for this source — the utility is checked as collected with the owner only.</p>
      )}
    </div>
  );
}

export default function RecordsUploadModal({ job, readOnly, onClose, onChanged, onSubmitted }) {
  const [files, setFiles] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [pending, setPending] = useState([]);           // dropped, not yet uploaded
  const [tag, setTag] = useState(emptyTag);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(null);     // "2 of 5"
  const inputRef = useRef();

  const [editingId, setEditingId] = useState(null);
  const [editTag, setEditTag] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [failedSubmission, setFailedSubmission] = useState(null); // zip built but not in Site Tracker

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/records/${job.sf_id}/staged`);
      setFiles(data.files);
      setSubmissions(data.submissions);
      const stuck = data.submissions.find((s) => !s.pushed_to_sf && !s.purged_at);
      if (stuck) setFailedSubmission(stuck);
    } catch {
      setError('Failed to load staged records');
    } finally {
      setLoading(false);
    }
  }, [job.sf_id]);

  useEffect(() => { load(); }, [load]);

  function addFiles(list) {
    const arr = Array.from(list);
    const valid = arr.filter((f) => ALLOWED_EXTS.includes(f.name.split('.').pop().toLowerCase()));
    if (arr.length - valid.length > 0) setError(`${arr.length - valid.length} file(s) skipped — unsupported type`);
    else setError('');
    setPending((prev) => [...prev, ...valid]);
  }

  async function handleAdd() {
    if (!pending.length) { setError('Drop at least one file first'); return; }
    if (!tag.owner.trim() && !window.confirm('No utility owner entered. Add these files anyway?')) return;
    setError('');
    setNotice('');
    let done = 0;
    try {
      // One request per file — a big multi-file drop would otherwise exceed the
      // per-request upload limit.
      for (const f of pending) {
        setUploading(`${done + 1} of ${pending.length}`);
        const fd = new FormData();
        fd.append('files', f);
        Object.entries(tag).forEach(([k, v]) => fd.append(k, v));
        await api.post(`/records/${job.sf_id}/staged`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        done++;
      }
      setPending([]);
      setNotice(`${done} file(s) added to ${job.project_number}`);
      await load();
      onChanged && onChanged();
    } catch (err) {
      const msg = err.response?.data?.error || 'Upload failed';
      setError(done ? `${msg} (${done} of ${pending.length} added)` : msg);
      setPending((prev) => prev.slice(done));
      await load();
      onChanged && onChanged();
    } finally {
      setUploading(null);
    }
  }

  function startEdit(f) {
    setEditingId(f.id);
    setEditTag({ source: f.source, utility_type: f.utility_type, owner: f.owner || '', state: f.state || '', city: f.city || '', contact_name: f.contact_name || '', contact_info: f.contact_info || '' });
  }

  async function saveEdit() {
    setBusyId(editingId);
    try {
      const { data } = await api.patch(`/records/staged/${editingId}`, editTag);
      setFiles((prev) => prev.map((f) => (f.id === data.id ? data : f)));
      setEditingId(null);
      onChanged && onChanged();
    } catch (err) {
      setError(err.response?.data?.error || 'Update failed');
    } finally {
      setBusyId(null);
    }
  }

  async function remove(f) {
    if (!window.confirm(`Remove "${f.file_name}" from this job?`)) return;
    setBusyId(f.id);
    try {
      await api.delete(`/records/staged/${f.id}`);
      setFiles((prev) => prev.filter((x) => x.id !== f.id));
      onChanged && onChanged();
    } catch {
      setError('Remove failed');
    } finally {
      setBusyId(null);
    }
  }

  async function download(f) {
    setBusyId(f.id);
    try { await downloadBlob(`/records/staged/${f.id}/download`, f.file_name); }
    catch { setError('Download failed'); }
    finally { setBusyId(null); }
  }

  async function downloadZip(sub) {
    try { await downloadBlob(`/records/submissions/${sub.id}/download`, sub.zip_name); }
    catch { setError('Zip download failed'); }
  }

  // Every staged file in one zip, without submitting to Site Tracker
  async function downloadAll() {
    setDownloadingAll(true);
    setError('');
    try { await downloadBlob(`/records/${job.sf_id}/staged/download`, `${job.site_name || job.project_number} Utility Records.zip`); }
    catch { setError('Download failed'); }
    finally { setDownloadingAll(false); }
  }

  async function submit() {
    if (!files.length) return;
    const ok = window.confirm(
      `Submit ${files.length} record file(s) for ${job.project_number} to Site Tracker?\n\n` +
      `They will be zipped as "${(job.site_name || job.project_number)} Utility Records.zip" and the job will be marked Completed.`
    );
    if (!ok) return;
    setSubmitting(true);
    setError('');
    try {
      const { data } = await api.post(`/records/${job.sf_id}/submit`);
      try { await downloadZip(data.submission); } catch { /* zip is still in Site Tracker */ }
      onSubmitted && onSubmitted(data.job);
      onClose();
    } catch (err) {
      const body = err.response?.data;
      setError(body?.error || 'Submit failed');
      if (body?.submission) setFailedSubmission(body.submission);
      await load();
    } finally {
      setSubmitting(false);
    }
  }

  async function retryPush(sub) {
    setSubmitting(true);
    setError('');
    try {
      const { data } = await api.post(`/records/submissions/${sub.id}/retry`);
      setFailedSubmission(null);
      onSubmitted && onSubmitted(data.job);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Retry failed');
    } finally {
      setSubmitting(false);
    }
  }

  // Group for display the way the zip is organized: source → utility
  const groups = SOURCE_ORDER.map((src) => {
    const inSrc = files.filter((f) => f.source === src);
    const byUtil = {};
    for (const f of inSrc) (byUtil[f.utility_type] = byUtil[f.utility_type] || []).push(f);
    // Alphabetical, with "Multiple" last so it doesn't land between Gas and Pipeline
    const utilOrder = (u) => (u === 'Multiple' ? '\uffff' : u);
    return { src, utils: Object.entries(byUtil).sort(([a], [b]) => utilOrder(a).localeCompare(utilOrder(b))) };
  }).filter((g) => g.utils.length);

  const unsubmitted = files.filter((f) => !f.submitted_at).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
      <div className="w-full max-w-4xl rounded-xl border border-navy-500 p-6 max-h-[92vh] overflow-y-auto" style={{ backgroundColor: '#142236' }}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-white font-bold text-lg">📁 Utility Records</h2>
            <p className="text-gray-400 text-xs mt-0.5 font-mono">{job.project_number}{job.site_name ? ` — ${job.site_name}` : ''}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition text-xl leading-none">&times;</button>
        </div>

        {/* Add records */}
        {!readOnly && (
          <div className="rounded-lg border p-4 mb-5" style={{ borderColor: '#1e3a5f', backgroundColor: '#0d1b2e' }}>
            <h3 className="text-xs text-gray-400 uppercase tracking-widest mb-3">Add records</h3>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
              onClick={() => inputRef.current?.click()}
              className="border-2 border-dashed rounded-lg p-5 text-center cursor-pointer transition mb-3"
              style={{ borderColor: dragging ? '#c9a84c' : '#1e3a5f', backgroundColor: dragging ? '#1a2d47' : 'transparent' }}
            >
              <p className="text-gray-300 text-sm">Drop utility record files here, or click to browse</p>
              <p className="text-gray-600 text-xs mt-1">PDF, images, DWG, KMZ/KML, SHP, ZIP · up to 100MB each</p>
              <input ref={inputRef} type="file" multiple accept={ALLOWED} className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
            </div>
            {pending.length > 0 && (
              <ul className="mb-3 space-y-1">
                {pending.map((f, i) => (
                  <li key={i} className="flex items-center justify-between rounded px-3 py-1.5 text-sm" style={{ backgroundColor: '#0f1a29' }}>
                    <span className="text-white truncate">{f.name} <span className="text-gray-500 text-xs">{formatSize(f.size)}</span></span>
                    <button onClick={() => setPending((p) => p.filter((_, idx) => idx !== i))} className="text-gray-500 hover:text-red-400 text-xs ml-2">✕</button>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-[11px] text-gray-500 mb-2">These details apply to every file in this drop. You can retag any file afterward.</p>
            <TagForm tag={tag} onChange={setTag} />
            <div className="flex items-center gap-3 mt-3">
              <button
                onClick={handleAdd}
                disabled={!!uploading || !pending.length}
                className="px-4 py-2 rounded font-bold uppercase tracking-wide text-sm transition disabled:opacity-40"
                style={{ backgroundColor: '#c9a84c', color: '#0d1b2e' }}
              >
                {uploading ? `Adding ${uploading}…` : `Add ${pending.length || ''} file${pending.length === 1 ? '' : 's'} to job`}
              </button>
              {notice && <span className="text-xs text-green-400">{notice}</span>}
            </div>
          </div>
        )}

        {error && <p className="text-red-400 text-sm mb-3">{error}</p>}

        {/* Staged files */}
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs text-gray-400 uppercase tracking-widest">
            Records on the site {files.length ? `· ${files.length}` : ''}
            {unsubmitted > 0 && files.length !== unsubmitted && <span className="text-gold-500 normal-case tracking-normal ml-2" style={{ color: '#c9a84c' }}>{unsubmitted} not yet submitted</span>}
          </h3>
          {files.length > 0 && (
            <button
              onClick={downloadAll}
              disabled={downloadingAll}
              title="Download every staged file as one zip — does not submit to Site Tracker"
              className="text-xs px-3 py-1 rounded border text-gray-300 hover:text-white hover:border-gray-400 transition disabled:opacity-40"
              style={{ borderColor: '#1e3a5f' }}
            >
              {downloadingAll ? 'Zipping…' : '⬇ Download all'}
            </button>
          )}
        </div>
        {loading ? (
          <p className="text-gray-500 text-sm py-4 text-center">Loading…</p>
        ) : !files.length ? (
          <p className="text-gray-500 text-sm py-4 text-center">No records staged for this job yet.</p>
        ) : (
          <div className="space-y-4">
            {groups.map((g) => (
              <div key={g.src}>
                <div className="text-xs font-bold text-white mb-1">{SOURCE_LABEL[g.src]}</div>
                {g.utils.map(([util, list]) => (
                  <div key={util} className="ml-3 mb-2">
                    <div className="text-[11px] uppercase tracking-wider text-gray-400 mb-1">{util}</div>
                    <ul className="space-y-1">
                      {list.map((f) => (
                        <li key={f.id} className="rounded px-3 py-2" style={{ backgroundColor: '#0f1a29' }}>
                          <div className="flex items-center gap-3">
                            <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded shrink-0" style={{ color: '#c9a84c', backgroundColor: '#1e3a5f' }}>{f.file_type}</span>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm text-white truncate">{f.file_name} <span className="text-gray-500 text-xs">{formatSize(f.size_bytes)}</span></div>
                              <div className="text-xs text-gray-400 truncate">
                                {f.owner || <span className="text-gray-600 italic">no owner</span>}
                                {(f.city || f.state) && ` · ${[f.city, f.state].filter(Boolean).join(', ')}`}
                                {f.source === 'owner' && (f.contact_name || f.contact_info) && ` · ${[f.contact_name, f.contact_info].filter(Boolean).join(' · ')}`}
                              </div>
                              <div className="text-[11px] text-gray-600">
                                {f.uploaded_by_name || 'Unknown'} · {fmtDateTime(f.created_at)}
                                {f.submitted_at ? <span className="text-green-500 ml-2">✓ submitted {fmtDateTime(f.submitted_at)}</span> : <span className="ml-2" style={{ color: '#c9a84c' }}>staged</span>}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0 text-xs">
                              <button onClick={() => download(f)} disabled={busyId === f.id} className="text-gray-400 hover:text-white transition" title="Download">⬇</button>
                              {!readOnly && (
                                <>
                                  <button onClick={() => (editingId === f.id ? setEditingId(null) : startEdit(f))} disabled={busyId === f.id} className="text-gray-400 hover:text-white transition" title="Retag">✎</button>
                                  <button onClick={() => remove(f)} disabled={busyId === f.id} className="text-gray-500 hover:text-red-400 transition" title="Remove">✕</button>
                                </>
                              )}
                            </div>
                          </div>
                          {editingId === f.id && editTag && (
                            <div className="mt-3 pt-3 border-t" style={{ borderColor: '#1e3a5f' }}>
                              <TagForm tag={editTag} onChange={setEditTag} compact />
                              <div className="flex gap-2 mt-2">
                                <button onClick={saveEdit} disabled={busyId === f.id} className="px-3 py-1 rounded text-xs font-bold" style={{ backgroundColor: '#c9a84c', color: '#0d1b2e' }}>Save</button>
                                <button onClick={() => setEditingId(null)} className="px-3 py-1 rounded text-xs border text-gray-400 hover:text-white" style={{ borderColor: '#1e3a5f' }}>Cancel</button>
                              </div>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Previous submissions */}
        {submissions.length > 0 && (
          <div className="mt-5">
            <h3 className="text-xs text-gray-400 uppercase tracking-widest mb-2">Submitted to Site Tracker</h3>
            <ul className="space-y-1">
              {submissions.map((s) => (
                <li key={s.id} className="flex items-center justify-between rounded px-3 py-2 text-xs" style={{ backgroundColor: '#0f1a29' }}>
                  <div className="min-w-0">
                    <div className="text-white truncate">{s.zip_name} <span className="text-gray-500">{formatSize(s.size_bytes)} · {s.file_count} files</span></div>
                    <div className="text-gray-500">{s.submitted_by_name || 'Unknown'} · {fmtDateTime(s.created_at)}
                      {s.pushed_to_sf ? <span className="text-green-500 ml-2">✓ in Site Tracker</span> : <span className="text-red-400 ml-2">⚠ not in Site Tracker</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0 ml-3">
                    {!s.pushed_to_sf && !readOnly && !s.purged_at && (
                      <button onClick={() => retryPush(s)} disabled={submitting} className="font-bold" style={{ color: '#c9a84c' }}>Retry push</button>
                    )}
                    {(!s.purged_at || s.in_site_tracker) && (
                      <button onClick={() => downloadZip(s)} className="text-gray-300 hover:text-white">⬇ zip</button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Footer */}
        <div className="flex gap-3 mt-6">
          {readOnly ? (
            <button onClick={onClose} className="flex-1 py-2 rounded border border-navy-500 text-gray-300 text-sm hover:text-white hover:border-gray-400 transition">Close</button>
          ) : (
            <>
              <button
                onClick={submit}
                disabled={submitting || !files.length || !!uploading}
                title={files.length ? 'Zip every staged file (plus the contact report) and attach it to the job in Site Tracker' : 'Add records first'}
                className="flex-1 py-2 rounded font-bold uppercase tracking-wide text-sm transition disabled:opacity-40"
                style={{ backgroundColor: '#c9a84c', color: '#0d1b2e' }}
              >
                {submitting ? 'Submitting…' : failedSubmission ? 'Rebuild & submit to Site Tracker' : 'Submit to Site Tracker'}
              </button>
              <button onClick={onClose} disabled={submitting} className="px-4 py-2 rounded border border-navy-500 text-gray-400 text-sm hover:text-white hover:border-gray-400 transition">Close</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
